import { useSelector } from 'react-redux';

import dataTables from 'view/dataTables';
import { dataConfig } from 'data/dataConfig';
import { DATA_SOURCES, getAppRoute } from 'data/dataSourceConstants';
import { buildInProgressTaskNames, isDataSetLoading } from 'utils/firstLoadTasks';
import { isAdminUser } from 'utils/userUtils';
import { isDataSetInCoveredBorough } from 'utils/dataSetBoroughCoverage';

import { getLabel } from 'utils/dataSetLinkUtils';
import {
	isPrimarySourcedAnchorDataSet,
	shouldHideStandaloneDataSetLink,
	getPrimarySourcedMenuConfigForDataSet,
	getPrimarySourcedMenuLabel,
	getPrimarySourcedIssueCount,
} from 'utils/primarySourcedDataSets';
import Badge from 'components/Badge';
import Collapse from 'components/Collapse';
import DataSetLoadingIcon from 'components/DataSetLoadingIcon';

import './DataSetsList.css';

// Shows single-digit counts as two digits ("02") so the sidebar pills line up
const padCount = (count) => String(count).padStart(2, '0');

function getGrouping(dataSet, config) {
	if (config.dataCategory === 'violation') {
		if (config.grouping) {
			return config.grouping;
		}
		return config.title;
	}
	return config.title;
}

export const buildDataSetItem = (dataSet, dataSource, propertyData) => {
	let config = dataTables[dataSet];
	if (config[dataSource]) {
		let openIssueCountField =
			dataConfig[dataSet]?.openIssueCountFields[dataSource];
		let openIssueCount = propertyData?.[openIssueCountField] || 0;
		let label = getLabel(config, dataSource, dataSet);
		let dataSetRoute = getAppRoute(dataSet, dataSource);

		return {
			url: dataSetRoute,
			label,
			grouping: getGrouping(dataSet, config),
			config,
			dataSet,
			dataSource,
			openIssueCount,
			dataConfig: dataConfig[dataSet],
		};
	}
};

const fdnySpecial = {
	fdnyViolations: true,
	fdnyInspections: true,
	// fdnyPermits: true,
	fdnySummaries: true,
};

// const combinedDataSets = {
// 	// dobComplaintsCombined: true,
// 	// dobViolationsCombined: true,
// 	// ecbViolationsCombined: true,
// 	// jobApplicationsCombined: true,
// };

function buildPrimarySourcedMenuItem(dataSet, propertyData) {
	const menuConfig = getPrimarySourcedMenuConfigForDataSet(dataSet);
	const { dataSet: defaultDataSet, dataSource: defaultSource } =
		menuConfig.defaultEntry;
	const item = buildDataSetItem(defaultDataSet, defaultSource, propertyData);
	if (!item) {
		return null;
	}
	return {
		...item,
		key: `${defaultDataSet}-grouped`,
		dataSet: defaultDataSet,
		dataSource: defaultSource,
		label: getPrimarySourcedMenuLabel(menuConfig),
		openIssueCount: getPrimarySourcedIssueCount(menuConfig, propertyData),
		isPrimarySourced: true,
	};
}

function buildLegacyJobApplicationItem(dataSource, propertyData) {
	const legacyConfig = dataTables.jobApplicationsLegacy;
	if (!legacyConfig?.[dataSource]) {
		return null;
	}
	const item = buildDataSetItem('jobApplications', dataSource, propertyData);
	if (!item) {
		return null;
	}
	return {
		...item,
		key: `jobApplications-legacy-${dataSource}`,
		config: legacyConfig,
		label: getLabel(legacyConfig, dataSource),
		isLegacyJobApplications: true,
	};
}

function buildLegacyJobApplicationItems(menuConfig, propertyData) {
	return menuConfig.entries
		.map((entry) =>
			buildLegacyJobApplicationItem(entry.dataSource, propertyData),
		)
		.filter(Boolean);
}

function getLinks(dataCategory, propertyData, fdnyOnly, user) {
	let isAdmin = isAdminUser(user);
	let items = [];
	const addedPrimarySourced = new Set();
	Object.keys(dataTables).forEach((dataSet) => {
		// Out-of-coverage data sets are hidden entirely for this property. An empty
		// row here would claim the property was checked and found clear, which for a
		// borough the source does not index is a claim we cannot make.
		if (!isDataSetInCoveredBorough(dataSet, propertyData)) {
			return;
		}

		let config = dataTables[dataSet];

		if (config.sidebarOnly) {
			return;
		}

		if (shouldHideStandaloneDataSetLink(dataSet)) {
			return;
		}

		//data source sub-loop
		DATA_SOURCES.forEach((dataSource) => {
			if (config[dataSource]) {
				if (config.isHiddenFromNav) return; // hidden from sidebar
				if (config.dataCategory === dataCategory) {
					if (fdnyOnly) {
						//special case for handling 'archived' fdny datasets that are no longer provided by opendata
						if (fdnySpecial[dataSet]) {
							items.push(buildDataSetItem(dataSet, dataSource, propertyData));
						}
					} else if (!fdnySpecial[dataSet]) {
						if (isPrimarySourcedAnchorDataSet(dataSet)) {
							if (!addedPrimarySourced.has(dataSet)) {
								addedPrimarySourced.add(dataSet);
								const canShow =
									(config.isAdminOnly && isAdmin) || !config.isAdminOnly;
								if (canShow) {
									const menuConfig =
										getPrimarySourcedMenuConfigForDataSet(dataSet);
									const menuItem = buildPrimarySourcedMenuItem(
										dataSet,
										propertyData,
									);
									if (menuItem) {
										items.push(menuItem);
									}
									if (
										menuConfig &&
										isAdmin &&
										menuConfig.anchorDataSet === 'jobApplications'
									) {
										items.push(
											...buildLegacyJobApplicationItems(
												menuConfig,
												propertyData,
											),
										);
									}
								}
							}
							return;
						}
						if (config.isAdminOnly && isAdmin) {
							items.push(buildDataSetItem(dataSet, dataSource, propertyData));
						} else if (!config.isAdminOnly) {
							items.push(buildDataSetItem(dataSet, dataSource, propertyData));
						}

						//special case handling for combined datasets.
						// if (combinedDataSets[dataSet]) {
						// 	// make the collapse section and add the combined dataset
						// 	// this code assumes that the combined dataset comes before the legacy opendata/bis versions
						// 	items.push({
						// 		isCollapse: true,
						// 		key: `${config.title}-${dataSet}`,
						// 		title: config.title,
						// 		dataSet: dataSet,
						// 		items: [buildDataSetItem(dataSet, dataSource, propertyData)],
						// 		openIssueCount: propertyData?.[dataConfig[dataSet]?.openIssueCountFields?.[dataSource]],
						// 	});
						// } else if (combinedDataSets[dataSet + 'Combined']) {
						// 	// add 'legacy' opendata and bis datasets to collapse section with combined dataset
						// 	// this code assumes that the combined dataset comes ahead of the non-combined versions
						// 	// also assumes that the combined dataset has the same name as the legacy dataset, but with 'Combined' on the end
						// 	let combinedCollapse = items.find(item => item?.dataSet === dataSet + 'Combined');
						// 	combinedCollapse?.items.push(buildDataSetItem(dataSet, dataSource, propertyData));
						// } else {
						// 	//this is the non-special case for 'normal' datasets
						// 	items.push(buildDataSetItem(dataSet, dataSource, propertyData));
						// }
					}
				}
			}
		});
	});
	return items;
}

function DataSetsList({ category, onSelectDataSet, selectedDataSet }) {
	let propertyData = useSelector((state) => state.property.propertyData || {});
	let user = useSelector((state) => state.user.userData);
	let firstLoadTasksData = useSelector((state) => state.firstLoadTasks?.data);
	let inProgressTaskNames = buildInProgressTaskNames(firstLoadTasksData);

	let items = getLinks(category, propertyData, false, user);
	let fdnyArchiveItems = getLinks(category, propertyData, true, user);
	if (fdnyArchiveItems?.length) {
		items.push({
			isCollapse: true,
			title: 'FDNY (Archive)',
			key: 'FDNY (Archive)',
			items: fdnyArchiveItems,
		});
	}

	const isSelected = (item) => {
		if (item?.isPrimarySourced) {
			if (selectedDataSet?.tableView === 'legacy') {
				return false;
			}
			const selectedMenu = getPrimarySourcedMenuConfigForDataSet(
				selectedDataSet?.dataSet,
			);
			const itemMenu = getPrimarySourcedMenuConfigForDataSet(item.dataSet);
			if (!selectedMenu || !itemMenu) {
				return false;
			}
			if (item.dataSet === 'jobApplications') {
				return (
					selectedMenu.anchorDataSet === itemMenu.anchorDataSet &&
					selectedDataSet?.tableView === 'grouped'
				);
			}
			return selectedMenu.anchorDataSet === itemMenu.anchorDataSet;
		}
		if (item?.isLegacyJobApplications) {
			return (
				selectedDataSet?.dataSet === item.dataSet &&
				selectedDataSet?.dataSource === item.dataSource &&
				selectedDataSet?.tableView === 'legacy'
			);
		}
		return (
			item?.dataSet === selectedDataSet?.dataSet &&
			item?.dataSource === selectedDataSet?.dataSource
		);
	};

	// Loading until the row's scrape finishes. A loaded table is not "done": the overview
	// prefetches every in-progress data set seconds into the scrape.
	const isItemLoading = (item) =>
		isDataSetLoading(item.dataSet, item.dataSource, inProgressTaskNames);

	const linkElement = (item) => {
		let loading = isItemLoading(item);
		return (
			<div
				key={item.key || item.url}
				className={`data-set-link-wrapper ${
					isSelected(item) ? 'is-selected' : ''
				}`}
			>
				<button className="btn-hyperlink" onClick={() => onSelectDataSet(item)}>
					<span>
						<span>{item.label}</span>
						{loading ? (
							<DataSetLoadingIcon className="ms-2" />
						) : (
							item.openIssueCount > 0 && (
								<span className="ms-2 open-issue-badge-wrapper">
									<Badge count={padCount(item.openIssueCount)} color={'danger'} />
								</span>
							)
						)}
					</span>
				</button>
			</div>
		);
	};

	const collapseElement = (collapseItem) => (
		<Collapse title={collapseItem.title} key={collapseItem.key} initCollapsed>
			{collapseItem.items.map((item) => (
				<div
					key={item.key || item.url}
					className={`data-set-link-wrapper ${
						isSelected(item) ? 'is-selected' : ''
					}`}
				>
					<button
						className="btn-hyperlink"
						onClick={() => onSelectDataSet(item)}
					>
						<span>
							<span>{item.label}</span>
							{item.openIssueCount > 0 && (
								<span className="ms-2 open-issue-badge-wrapper">
									<Badge count={padCount(item.openIssueCount)} color={'danger'} />
								</span>
							)}
						</span>
					</button>
				</div>
			))}
		</Collapse>
	);

	return (
		<div className="data-sets-list">
			{items.map((item) => {
				if (item.isCollapse) {
					if (item.openIssueCount) {
						item.title = (
							<span>
								<span>{item.title}</span>
								<span className="ms-2 open-issue-badge-wrapper">
									<Badge count={padCount(item.openIssueCount)} color={'danger'} />
								</span>
							</span>
						);
					}
					return collapseElement(item);
				} else {
					return linkElement(item);
				}
			})}
		</div>
	);
}

export default DataSetsList;
